import { LEGAL_CONTACT_EMAIL } from "@/utils/legalDocs";

export default function PrivacyPolicyContent() {
  return (
    <>
      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Introduction</h2>
        <p>
          TerraKronos (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) is software for Hawaii real
          estate professionals. It helps you manage deal timelines, people, documents, Team
          coordination, Hawaii calculators, and related workflows. This Privacy Policy explains what
          information we collect, how we use it, and the choices you have when you use terrakronos.com,
          staging.terrakronos.com when we offer it, and related services (the &quot;Service&quot;).
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Information we collect</h2>
        <p className="mb-3">We collect information in these ways:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Account information:</strong> You can sign in with Google or with a one-time
            email sign-in link. From Google we receive your name, email address, and profile picture
            if Google sends them. From an email sign-in link we receive the email address you typed.
            We store a short-lived, hashed sign-in token so that link can be used once. We do not
            ask new accounts to set a password.
          </li>
          <li>
            <strong>Your card and people lists:</strong> Name, phone, email, license, brokerage, and
            other fields you save on your professional card, your personal people library, and, if
            you are on a Team, that Team&apos;s people library.
          </li>
          <li>
            <strong>Deal and Team data:</strong> Information you enter about properties, clocks and
            dates, notes, parties, money, templates, filled contract answers, generated contract
            PDFs, Team roles, and coordinators or teammates you add. Deal names, addresses, timeline
            labels, dates, notes, templates, and people-library cards are stored so the app can show
            them to you and to people you authorize.
          </li>
          <li>
            <strong>Documents:</strong> Files you upload and contract PDFs the Service creates. If
            you turn on encryption for a file, that file is encrypted on your device before we store
            it. We do not have your encryption passphrase or recovery phrase, and we cannot read
            those locked files. Encryption is not applied to every field. We can still see the
            deal information listed above unless a specific field was separately locked.
          </li>
          <li>
            <strong>Sharing and calendar links:</strong> When you create a share link or a calendar
            subscribe link, we store the token and the visibility you chose so those links can work.
            Anyone who has the link can see what you allowed on that link.
          </li>
          <li>
            <strong>Billing information:</strong> Paid plans are billed by Stripe. We receive
            subscription status, plan, and limited billing metadata. We do not receive your full
            card number.
          </li>
          <li>
            <strong>Email and notifications:</strong> We send service email (sign-in links,
            invitations, reminders, and similar messages) through our email providers (Resend and,
            when used, SendGrid). We store delivery-related preferences, such as unsubscribe choices
            for some shared-deal notices, and the notification settings you pick in the app.
          </li>
          <li>
            <strong>Technical data:</strong> Basic logs needed to run the Service, such as IP
            address, browser type, and error information, through our hosting providers. We also
            keep session records so you stay signed in.
          </li>
          <li>
            <strong>Public Hawaii Calculators:</strong> The calculators at /tools do not require an
            account and do not ask for your name. Hosting may still log technical data such as IP
            address, the same as other public pages.
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Cookies and browser storage</h2>
        <p>
          We use a session cookie and similar browser storage so you stay signed in (about seven
          days unless you sign out). We also store small preferences on your device, such as
          dashboard order. We do not use advertising cookies or third-party analytics pixels.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">How we use information</h2>
        <p>We use your information to:</p>
        <ul className="list-disc pl-5 space-y-2 mt-3">
          <li>Provide, maintain, and improve the Service</li>
          <li>Sign you in and keep your session</li>
          <li>Show your deals, clocks, people lists, and documents to you and to people you authorize</li>
          <li>Fill supported purchase-contract forms and create PDFs you request</li>
          <li>Send emails you request or that are part of the Service (for example sign-in links and deadline reminders)</li>
          <li>Create the spreadsheet downloads you request (your people list and deal clocks)</li>
          <li>Process subscriptions and enforce plan limits</li>
          <li>Respond to support requests and protect against abuse or fraud</li>
          <li>Comply with legal obligations</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">How we share information</h2>
        <p className="mb-3">
          We do not sell your personal information. We share data only as needed to run the Service,
          including with:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Service providers:</strong> Hosting (for example Vercel and Render), database
            (MongoDB Atlas), email (Resend and, when used, SendGrid), payments (Stripe), and Google
            if you sign in with Google. They process data under contracts that require them to
            protect it.
          </li>
          <li>
            <strong>People you authorize:</strong> Teammates, transaction coordinators, and others
            you invite or share with, according to the permissions and share settings you choose.
            A Team library is visible to people on that Team.
          </li>
          <li>
            <strong>Legal requirements:</strong> When required by law or to protect rights, safety, or
            security.
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Data retention</h2>
        <p>
          We keep your information while your account is active and as needed to provide the Service,
          resolve disputes, and meet legal requirements. Email sign-in links expire after a short
          time and can be used once. Deals you delete may remain in a deleted list for a limited
          period (about 30 days) before they are removed. You may request deletion of your account
          from Settings → Legal, or by emailing us. Some data may remain in backups for a limited
          period before it is removed.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Security</h2>
        <p>
          We use industry-standard measures to protect data in transit and at rest. No method of
          transmission or storage is completely secure. You are responsible for safeguarding access
          to your devices, sign-in email, share links, calendar links, and encryption recovery
          materials. If you lose both your encryption passphrase and your recovery phrase, we cannot
          unlock files that were encrypted with those secrets.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Your choices</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>Update your card, people lists, and notification settings in the app where available.</li>
          <li>
            Download a spreadsheet of your people list and deal clocks from Settings, Agent home,
            TC Dashboard, or a deal. That download is not a complete copy of every file or message.
          </li>
          <li>Revoke or change share links and calendar links you created.</li>
          <li>Use unsubscribe links in emails for certain shared-deal notifications.</li>
          <li>
            Request account deletion from Settings → Legal, or by emailing us. A Super Admin reviews
            the request, then erases the login, owned deals, people list, and templates. Official
            starters and other people&apos;s deals stay.
          </li>
          <li>Contact us to access, correct, or delete personal information, subject to applicable law.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Children</h2>
        <p>
          The Service is not intended for anyone under 18. We do not knowingly collect personal
          information from children.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Changes to this policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will post the revised version on
          this page and update the &quot;Last updated&quot; date. Continued use of the Service after
          changes means you accept the updated policy.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-foreground mb-2">Contact us</h2>
        <p>
          Questions about privacy:{" "}
          <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} className="text-primary hover:underline">
            {LEGAL_CONTACT_EMAIL}
          </a>
        </p>
      </section>
    </>
  );
}
